import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { X, Send, Loader2 } from "lucide-react";

/**
 *  Hidro — HidroVerse on-site AI assistant.
 *  ---------------------------------------------------------------
 *  Compact circular floating launcher at the bottom-right of the
 *  viewport — the only persistent surface the visitor sees from
 *  Hidro until they choose to engage. Click opens an inline chat
 *  panel anchored above the launcher.
 *
 *  Backend: POST /api/hydro/chat  body { session_id, message }
 *           GET  /api/hydro/history?session_id=…
 *  Persistence: session_id lives in localStorage (anonymous).
 * ---------------------------------------------------------------
 */

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "hidroverse_hydro_session";

function getOrCreateSessionId() {
  if (typeof window === "undefined") return "ssr";
  let id = window.localStorage.getItem(STORAGE_KEY);
  if (!id) {
    id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(STORAGE_KEY, id);
  }
  return id;
}

// Turn plain paths (/services, /tools/hydraulics, /contact) inside assistant text
// into <Link> elements, and bare https URLs / phones into anchors. Returns React nodes.
function renderAssistantText(text) {
  // Defensive: strip stray markdown the backend prompt forbids but a model may slip on.
  //   **bold**  ->  bold
  //   _italic_  ->  italic
  //   leading `# ` heading markers -> stripped
  const cleaned = String(text)
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/(^|\s)_([^_\n]+)_(?=\s|$|[.,;:!?])/g, "$1$2")
    .replace(/^#{1,6}\s+/gm, "");

  // Tokenise on whitespace boundaries, then map each token.
  const pattern = /(\bhttps?:\/\/[^\s)]+|\B\/[a-z][a-z0-9/_#-]*|\+?\d{1,3}\s?\d{2,3}\s?\d{3}\s?\d{3,4})/gi;
  const parts = [];
  let lastIdx = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(cleaned)) !== null) {
    if (match.index > lastIdx) {
      parts.push(<span key={`t${key++}`}>{cleaned.slice(lastIdx, match.index)}</span>);
    }
    const tok = match[0];
    if (tok.startsWith("http")) {
      parts.push(
        <a key={`a${key++}`} href={tok} target="_blank" rel="noreferrer" className="hydro-link">
          {tok}
        </a>,
      );
    } else if (tok.startsWith("/")) {
      // Internal path — split hash into pathname + hash so React Router gets a clean path.
      const hashIdx = tok.indexOf("#");
      const to = hashIdx >= 0
        ? { pathname: tok.slice(0, hashIdx), hash: tok.slice(hashIdx) }
        : tok;
      parts.push(
        <Link key={`l${key++}`} to={to} className="hydro-link">
          {tok}
        </Link>,
      );
    } else {
      // phone number
      const tel = tok.replace(/\s+/g, "");
      parts.push(
        <a key={`p${key++}`} href={`tel:${tel}`} className="hydro-link">
          {tok}
        </a>,
      );
    }
    lastIdx = match.index + tok.length;
  }
  if (lastIdx < cleaned.length) {
    parts.push(<span key={`t${key++}`}>{cleaned.slice(lastIdx)}</span>);
  }
  return parts;
}

const SUGGESTIONS = [
  "What services do you offer?",
  "Which industries do you work with?",
  "Where can I size a water tank?",
  "How do you start a project?",
];

export default function HydroChat() {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const [messages, setMessages] = useState([]);  // [{role,content}]
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionId = useRef(getOrCreateSessionId());
  const listEndRef = useRef(null);
  const inputRef = useRef(null);

  // Reveal delay so the cinematic intro is never interrupted.
  useEffect(() => {
    const t = setTimeout(() => setShown(true), 1400);
    return () => clearTimeout(t);
  }, []);

  // Rehydrate prior history from backend on first open.
  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/hydro/history?session_id=${encodeURIComponent(sessionId.current)}`);
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data.messages) && data.messages.length) {
        setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
      }
    } catch {
      /* network hiccup — chat will just start fresh */
    }
  }, []);

  useEffect(() => {
    if (open && messages.length === 0) loadHistory();
  }, [open, messages.length, loadHistory]);

  // Auto-scroll on new messages.
  useEffect(() => {
    if (open) listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, open, sending]);

  // Focus the input when chat opens.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const sendMessage = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setSending(true);
    try {
      const res = await fetch(`${API}/api/hydro/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: trimmed }),
      });
      if (!res.ok) throw new Error("bad status");
      const data = await res.json();
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I lost signal for a second — try again, or reach the team directly on +27 72 320 7326 or /contact.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!shown) return null;

  return (
    <>
      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[60] flex flex-col"
          data-testid="hydro-panel"
          style={{
            width: "min(380px, calc(100vw - 32px))",
            height: "min(560px, calc(100vh - 140px))",
            background: "#FFFFFF",
            border: "1px solid rgba(12,42,64,0.10)",
            borderRadius: 14,
            boxShadow: "0 24px 60px rgba(12,42,64,0.25), 0 1px 2px rgba(12,42,64,0.04)",
            overflow: "hidden",
            animation: "hydroReveal 220ms cubic-bezier(0.2,0.7,0.2,1)",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              background: "rgba(22,24,27,0.96)",
              color: "#F5F7FA",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="inline-flex items-center justify-center shrink-0"
                style={{
                  width: 32, height: 32, borderRadius: 999,
                  background: "rgba(31,182,193,0.15)",
                  border: "1px solid rgba(31,182,193,0.4)",
                }}
              >
                <span
                  className="inline-block w-[8px] h-[8px] rounded-full"
                  style={{ background: "#1FB6C1", boxShadow: "0 0 10px #1FB6C1" }}
                />
              </span>
              <div className="min-w-0">
                <div
                  className="font-display"
                  style={{ fontSize: "0.95rem", fontWeight: 600, letterSpacing: "-0.005em" }}
                >
                  Hidro
                </div>
                <div
                  className="font-mono uppercase"
                  style={{ fontSize: "0.56rem", letterSpacing: "0.22em", color: "rgba(245,247,250,0.55)" }}
                >
                  HidroVerse · on-site guide
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              data-testid="hydro-close"
              className="inline-flex items-center justify-center"
              style={{
                width: 32, height: 32, borderRadius: 8,
                background: "transparent", color: "rgba(245,247,250,0.8)",
                transition: "background 160ms ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <X size={18} />
            </button>
          </div>

          {/* Message list */}
          <div
            className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3"
            style={{ background: "#FAFAF7" }}
            data-testid="hydro-messages"
          >
            {messages.length === 0 && !sending && (
              <div className="flex flex-col gap-3">
                <div
                  className="self-start max-w-[88%] px-3.5 py-2.5"
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid rgba(12,42,64,0.08)",
                    borderRadius: "12px 12px 12px 4px",
                    color: "#0C2A40",
                    fontSize: "0.88rem",
                    lineHeight: 1.5,
                  }}
                >
                  Hi, I'm Hidro — HidroVerse's on-site guide. Ask me anything about our services, industries we serve, the engineering tools on the site, or just where to find something.
                </div>
                <div className="flex flex-col gap-1.5 mt-2">
                  <div
                    className="font-mono uppercase"
                    style={{ fontSize: "0.55rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.5)" }}
                  >
                    Try
                  </div>
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => sendMessage(s)}
                      data-testid={`hydro-suggestion-${SUGGESTIONS.indexOf(s)}`}
                      className="self-start text-left px-3 py-1.5 transition-colors"
                      style={{
                        background: "#FFFFFF",
                        border: "1px solid rgba(31,182,193,0.35)",
                        borderRadius: 999,
                        color: "#0C2A40",
                        fontSize: "0.78rem",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(31,182,193,0.08)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "#FFFFFF")}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={m.role === "user" ? "self-end max-w-[88%]" : "self-start max-w-[88%]"}
                style={{
                  background: m.role === "user" ? "#0C2A40" : "#FFFFFF",
                  color: m.role === "user" ? "#F5F7FA" : "#0C2A40",
                  border: m.role === "user" ? "1px solid #0C2A40" : "1px solid rgba(12,42,64,0.08)",
                  borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                  padding: "9px 14px",
                  fontSize: "0.88rem",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
                data-testid={`hydro-msg-${m.role}`}
              >
                {m.role === "assistant" ? renderAssistantText(m.content) : m.content}
              </div>
            ))}

            {sending && (
              <div
                className="self-start inline-flex items-center gap-2 px-3.5 py-2.5"
                style={{
                  background: "#FFFFFF",
                  border: "1px solid rgba(12,42,64,0.08)",
                  borderRadius: "12px 12px 12px 4px",
                  color: "rgba(12,42,64,0.65)",
                  fontSize: "0.82rem",
                }}
                data-testid="hydro-typing"
              >
                <Loader2 size={14} className="animate-spin" />
                <span>Hidro is thinking…</span>
              </div>
            )}

            <div ref={listEndRef} />
          </div>

          {/* Composer */}
          <div
            className="flex items-end gap-2 px-3 py-3"
            style={{ background: "#FFFFFF", borderTop: "1px solid rgba(12,42,64,0.08)" }}
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Ask Hidro anything…"
              data-testid="hydro-input"
              className="flex-1 min-w-0 resize-none px-3 py-2 outline-none"
              style={{
                background: "#FAFAF7",
                border: "1px solid rgba(12,42,64,0.12)",
                borderRadius: 10,
                color: "#0C2A40",
                fontSize: "0.9rem",
                lineHeight: 1.4,
                maxHeight: 120,
              }}
            />
            <button
              type="button"
              onClick={() => sendMessage()}
              disabled={sending || !input.trim()}
              aria-label="Send message"
              data-testid="hydro-send"
              className="inline-flex items-center justify-center shrink-0 transition-all"
              style={{
                width: 40, height: 40, borderRadius: 10,
                background: sending || !input.trim() ? "rgba(12,42,64,0.18)" : "#1FB6C1",
                color: sending || !input.trim() ? "rgba(255,255,255,0.7)" : "#070A0D",
                cursor: sending || !input.trim() ? "not-allowed" : "pointer",
              }}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Floating launcher — clean circular AI button at bottom-right */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close Hidro" : "Open Hidro — site assistant"}
        data-testid="hydro-toggle"
        className="fixed bottom-6 right-6 z-[61] inline-flex items-center justify-center group"
        style={{
          width: 56,
          height: 56,
          borderRadius: 999,
          background: open
            ? "#0C2A40"
            : "linear-gradient(135deg, #0C2A40 0%, #163E5C 60%, #1FB6C1 140%)",
          color: "#F5F7FA",
          border: "1px solid rgba(31,182,193,0.55)",
          boxShadow:
            "0 12px 32px rgba(12,42,64,0.40), 0 0 0 1px rgba(31,182,193,0.10) inset, 0 0 24px rgba(31,182,193,0.18)",
          transition: "transform 220ms cubic-bezier(0.2,0.7,0.2,1), box-shadow 220ms ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.06)";
          e.currentTarget.style.boxShadow =
            "0 16px 38px rgba(12,42,64,0.46), 0 0 0 1px rgba(31,182,193,0.18) inset, 0 0 32px rgba(31,182,193,0.32)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow =
            "0 12px 32px rgba(12,42,64,0.40), 0 0 0 1px rgba(31,182,193,0.10) inset, 0 0 24px rgba(31,182,193,0.18)";
        }}
      >
        {open ? (
          <X size={20} strokeWidth={2} />
        ) : (
          <>
            {/* Soft pulse halo to draw the eye without being noisy */}
            <span aria-hidden className="hydro-pulse" />
            <span
              className="font-display"
              style={{
                fontSize: "0.95rem",
                fontWeight: 600,
                letterSpacing: "-0.01em",
                lineHeight: 1,
                position: "relative",
                zIndex: 1,
              }}
            >
              H
            </span>
          </>
        )}
      </button>

      <style>{`
        @keyframes hydroReveal {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes hydroPulse {
          0%   { transform: scale(0.85); opacity: 0.65; }
          70%  { transform: scale(1.6);  opacity: 0;    }
          100% { transform: scale(1.6);  opacity: 0;    }
        }
        .hydro-pulse {
          position: absolute;
          inset: 0;
          border-radius: 999px;
          background: rgba(31, 182, 193, 0.28);
          animation: hydroPulse 2.2s cubic-bezier(0.2, 0.7, 0.2, 1) infinite;
        }
        .hydro-link {
          color: #0C2A40;
          font-weight: 600;
          text-decoration: underline;
          text-decoration-color: rgba(31,182,193,0.6);
          text-underline-offset: 2px;
          transition: color 160ms ease;
        }
        .hydro-link:hover { color: #1FB6C1; }
      `}</style>
    </>
  );
}
